import { useRoute, Link } from "wouter";
import { getBlogPost } from "@/data/blogPosts";
import { Calendar, Clock, ArrowLeft, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Streamdown } from "streamdown";
import { toast } from "sonner";
import NotFound from "./NotFound";
import SEO from "@/components/SEO";

export default function BlogPost() {
  const [, params] = useRoute("/blog/:slug");
  const post = params?.slug ? getBlogPost(params.slug) : undefined;

  if (!post) {
    return <NotFound />;
  }

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url,
        });
      } catch (err) {
        // User cancelled share or error occurred
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard!");
      } catch (err) {
        toast.error("Failed to copy link");
      }
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title={`${post.title} - Stanford Logistics Management`}
        description={post.excerpt}
        keywords={`${post.category}, trucking industry, logistics, ${post.title}`}
        article={true}
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground py-12 lg:py-16">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            {post.image && (
              <div className="mb-8 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-64 lg:h-96 object-cover"
                  fetchPriority="high"
                />
              </div>
            )}
            <Link href="/blog">
              <a className="inline-flex items-center space-x-2 text-primary-foreground/80 hover:text-primary-foreground transition-colors mb-6">
                <ArrowLeft size={20} />
                <span>Back to Insights</span>
              </a>
            </Link>
            
            <div className="flex items-center space-x-4 text-sm text-primary-foreground/80 mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-red-accent text-red-accent-foreground font-medium">
                {post.category}
              </span>
              <div className="flex items-center space-x-1">
                <Calendar size={14} />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </time>
              </div>
              <div className="flex items-center space-x-1">
                <Clock size={14} />
                <span>{post.readTime}</span>
              </div>
            </div>

            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl mb-4">
              {post.title}
            </h1>

            <p className="text-lg text-primary-foreground/90 leading-relaxed mb-6">
              {post.excerpt}
            </p>

            <div className="flex items-center justify-between">
              <p className="text-sm text-primary-foreground/70">
                By {post.author}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShare}
                className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
              >
                <Share2 size={16} className="mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 lg:py-16 bg-background">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none
              prose-headings:text-foreground prose-headings:font-bold
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-accent prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:text-foreground/80 prose-ul:my-6
              prose-ol:text-foreground/80 prose-ol:my-6
              prose-li:my-2
              prose-blockquote:border-l-accent prose-blockquote:bg-muted/30 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:my-6
              prose-code:text-accent prose-code:bg-muted/50 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-muted prose-pre:border prose-pre:border-border
            ">
              <Streamdown>{post.content}</Streamdown>
            </div>

            {/* Back to Blog CTA */}
            <div className="mt-12 pt-8 border-t border-border">
              <Link href="/blog">
                <Button variant="outline" size="lg" className="group">
                  <ArrowLeft className="mr-2 h-4 w-4 transition-transform group-hover:-translate-x-1" />
                  Back to All Insights
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
